import { inject, Injectable } from "@angular/core";
import {
  FloorplanItemDetailsModal,
  ModalProductItem,
} from "@components/floorplan-item-attachments-modal/floorplan-item-details-modal.component";
import { FloorplanMonitoringItemDetailsModalComponent } from "@components/floorplan-monitoring-item-details-modal/floorplan-monitoring-item-details-modal.component";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { UtilityProductService } from "@domain/product/monitoring/utility-product.service";
import { AlarmDeviceConfiguration } from "@domain/project/configurations/alarm-device-configuration";
import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { DisplayConfiguration } from "@domain/project/configurations/monitoring/display-configuration";
import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { UtilityConfiguration } from "@domain/project/configurations/monitoring/utility-configuration";
import { PlasticSignConfiguration } from "@domain/project/configurations/plastic-sign-configuration";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { SignalElementConfiguration } from "@domain/project/configurations/signal-element-configuration";
import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { ModalService, ModalSize } from "@odx/angular/components/modal";
import {
  buildCentralStationGroupedSourceItems,
  buildDisplayGroupedSourceItems,
  buildKvmGroupedSourceItems,
  buildPatientMonitorGroupedSourceItems,
  buildTelemetryGroupedSourceItems,
  buildUtilityGroupedSourceItems,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts-source-item.factory";
import {
  getCentralStationGroupedPartLabels,
  getDisplayGroupedPartLabels,
  getKvmGroupedPartLabels,
  getPatientMonitorGroupedPartLabels,
  getTelemetryGroupedPartLabels,
  getUtilityGroupedPartLabels,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringGroupedPartSourceItem } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.types";
import { ProductItem } from "@project/product-item";
import { of } from "rxjs";

@Injectable({ providedIn: "root" })
export class ConfigurationDetailsModalService {
  private readonly modalService = inject(ModalService);
  private readonly utilityProductService = inject(UtilityProductService);

  private readonly patientMonitorCategoryLabels = getPatientMonitorGroupedPartLabels();
  private readonly centralStationCategoryLabels = getCentralStationGroupedPartLabels();
  private readonly telemetryCategoryLabels = getTelemetryGroupedPartLabels();
  private readonly kvmCategoryLabels = getKvmGroupedPartLabels();
  private readonly displayCategoryLabels = getDisplayGroupedPartLabels();
  private readonly utilityCategoryLabels = getUtilityGroupedPartLabels();

  open(config: ProductConfiguration) {
    const monitoringCategory = this.resolveMonitoringCategory(config);
    if (monitoringCategory) {
      this.resolveTitle(config).subscribe((title) => {
        this.modalService.open(FloorplanMonitoringItemDetailsModalComponent, {
          id: "monitoring-item-details-modal",
          size: ModalSize.LARGE,
          data: {
            title,
            items: this.buildMonitoringItems(config),
            category: monitoringCategory,
          },
        });
      });
      return;
    }

    const { productItems, attachments, gasId } = this.createFgdsModalData(config);
    this.modalService
      .open(FloorplanItemDetailsModal, {
        id: "fgds-item-details-modal",
        size: ModalSize.LARGE,
        data: {
          title: config.name,
          gasId,
          productItems,
          attachments,
          customProducts: [],
        },
      })
      .onClose$.subscribe();
  }

  private createFgdsModalData(config: ProductConfiguration): {
    productItems: ModalProductItem[];
    attachments: ModalProductItem[];
    gasId?: string;
  } {
    if (config instanceof AlarmDeviceConfiguration) {
      return {
        productItems: config.productIds.map((id) => ProductItem.create(id)),
        attachments: config.attachments,
      };
    }
    if (config instanceof SignalElementConfiguration || config instanceof PlasticSignConfiguration) {
      return {
        productItems: [ProductItem.create(config.productId)],
        attachments: [],
      };
    }
    if (config instanceof GasWarningCenterConfiguration) {
      if (config.regard3000Configuration) {
        const productItems = [
          ...config.regard3000Configuration.baseUnits,
          ...(config.regard3000Configuration.inputModules ?? []),
          ...(config.regard3000Configuration.relayModules ?? []),
          ...(config.regard3000Configuration.gatewayModules ?? []),
          ...(config.regard3000Configuration.lidUnits ?? []),
        ];
        const slotCovers = config.regard3000Configuration.slotCovers;
        if (slotCovers) {
          productItems.push(slotCovers);
        }
        return {
          productItems,
          attachments: config.attachments,
        };
      }
      if (config.regard7000PlusConfiguration) {
        return {
          productItems: [
            ...config.regard7000PlusConfiguration.dockingStations,
            ...config.regard7000PlusConfiguration.inputModules,
            ...config.regard7000PlusConfiguration.relayModules,
            ...config.regard7000PlusConfiguration.gatewayModules,
            ...config.regard7000PlusConfiguration.terminalBlocks,
            ...config.regard7000PlusConfiguration.dashboards,
          ],
          attachments: config.attachments,
        };
      }
      return {
        productItems: [ProductItem.create(config.productId)],
        attachments: config.attachments,
      };
    }
    if (config instanceof TransmitterConfiguration) {
      return {
        productItems: [
          {
            ...ProductItem.create(config.productId),
            detectionRange: config.defaultDetectionRange,
          },
        ],
        attachments: config.attachments,
        gasId: config.gasId,
      };
    }
    if (config instanceof FlameDetectorConfiguration) {
      return {
        productItems: [
          {
            ...ProductItem.create(config.variantId ?? []),
          },
        ],
        attachments: config.attachments,
        gasId: config.substanceId,
      };
    }

    return { productItems: [], attachments: [] };
  }

  private resolveMonitoringCategory(config: ProductConfiguration) {
    if (config instanceof PatientMonitorConfiguration) return MonitoringCategory.PATIENT_MONITORS;
    if (config instanceof CentralStationConfiguration) return MonitoringCategory.CENTRAL_STATIONS;
    if (config instanceof TelemetryConfiguration) return MonitoringCategory.TELEMETRY;
    if (config instanceof KvmConfiguration) return MonitoringCategory.KVMS;
    if (config instanceof DisplayConfiguration) return MonitoringCategory.DISPLAYS;
    if (config instanceof UtilityConfiguration) return MonitoringCategory.UTILITY;
    return undefined;
  }

  private resolveTitle(config: ProductConfiguration) {
    if (config instanceof UtilityConfiguration) {
      return this.utilityProductService.resolveName(config);
    }
    return of(config.name);
  }

  private buildMonitoringItems(config: ProductConfiguration): MonitoringGroupedPartSourceItem[] {
    if (config instanceof PatientMonitorConfiguration) {
      return buildPatientMonitorGroupedSourceItems(
        {
          family: config.family,
          sensor: config.sensor,
          mountingGroup: config.mountingGroup,
          mountingArm: config.mountingArm,
          attachments: config.attachments,
        },
        this.patientMonitorCategoryLabels,
      );
    }
    if (config instanceof CentralStationConfiguration) {
      return buildCentralStationGroupedSourceItems(
        {
          baseOptions: config.baseOptions,
          licence: config.licence,
          additionalLicences: config.additionalLicences,
          accessories: config.accessories,
        },
        this.centralStationCategoryLabels,
      );
    }
    if (config instanceof TelemetryConfiguration) {
      return buildTelemetryGroupedSourceItems(
        {
          family: config.family,
          sensor: config.sensor,
          additionalBatteries: config.additionalBatteries,
        },
        this.telemetryCategoryLabels,
      );
    }
    if (config instanceof KvmConfiguration) {
      return buildKvmGroupedSourceItems(
        {
          transmitter: config.transmitter,
          receiver: config.receiver,
          accessories: config.accessories,
        },
        this.kvmCategoryLabels,
      );
    }
    if (config instanceof DisplayConfiguration) {
      return buildDisplayGroupedSourceItems(
        {
          display: config.display,
          accessories: config.accessories,
        },
        this.displayCategoryLabels,
      );
    }
    if (config instanceof UtilityConfiguration) {
      return buildUtilityGroupedSourceItems(
        {
          devices: config.devices,
        },
        this.utilityCategoryLabels,
      );
    }
    return [];
  }
}
